<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { AudioEngine } from "./lib/audio";
  import { loadQuote } from "./lib/api";
  import type { Quote } from "./lib/types";
  import { formatQuote } from "./lib/tracks";

  const SCENES = [
    "/images/main.webp",
    "/images/jazz.webp",
    "/images/pomodro.webp",
    "/images/sleep.webp",
    "/images/synthwave.webp",
    "/images/cozy-cat-cafe-with-coffee.webp",
    "/images/lofi-girl-college-scholarships-1920x1131.webp",
    "/images/469076083_orig.webp"
  ];

  const AUDIO_TRACKS = [
    "/audio/aventure-lofi-chill-nostalgic-469629.mp3",
    "/audio/bfcmusic-lofi-lo-fi-511230.mp3",
    "/audio/delosound-lofi-lofi-chill-lofi-girl-456265.mp3",
    "/audio/delosound-lofi-lofi-chill-lofi-girl-466467.mp3",
    "/audio/delosound-lofi-lofi-chill-lofi-girl-471138.mp3",
    "/audio/desifreemusic-cozy-lofi-background-music-for-relax-study-amp-sleep-453302.mp3",
    "/audio/freemusicforvideo-lofi-chill-music-495628.mp3",
    "/audio/leberch-lofi-vlog-525299.mp3",
    "/audio/lemonmusiclab-lofi-beat-relax-499261.mp3",
    "/audio/lofi_music_library-coffee-lofi-chill-lofi-ambient-458901.mp3",
    "/audio/lofi_music_library-lofi-girl-chill-lofi-beats-lofi-ambient-461871.mp3",
    "/audio/lofi_music_library-rainy-lofi-city-lofi-music-458076.mp3",
    "/audio/mondamusic-lofi-lofi-girl-lofi-music-529555.mp3",
    "/audio/prettyjohn1-lofi-523178.mp3",
    "/audio/prettyjohn1-lofi-chill-chill_38sec-490468.mp3",
    "/audio/pulsebox-lofi-melody-522894.mp3",
    "/audio/pulsebox-lofi-mood-522871.mp3",
    "/audio/pulsebox-lofi-night-522890.mp3",
    "/audio/pulsebox-lofi-vinyl-522882.mp3",
    "/audio/vibehorn-lofi-chill-background-461490.mp3"
  ];

  let isReady = false;
  let isPlaying = false;
  let sceneIndex = 0;
  let storyLine = "";
  let storyVisible = false;
  let audio: AudioEngine;
  let quoteRequestId = 0;

  function pickRandomTrack(exclude: string): string {
    const pool = AUDIO_TRACKS.filter((t) => t !== exclude);
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function setQuote(quote: Quote | null | undefined): void {
    storyLine = formatQuote(quote);
  }

  function refreshQuote(): void {
    const requestId = ++quoteRequestId;
    loadQuote().then((quote) => {
      if (requestId !== quoteRequestId) return;
      setQuote(quote);
      storyVisible = true;
    });
  }

  async function startExperience(): Promise<void> {
    await audio.init();
    if (!audio.isPlaying) throw new Error("Autoplay blocked");
    isReady = true;
    isPlaying = true;
  }

  async function tryAutoplay(): Promise<void> {
    try {
      await startExperience();
    } catch {
      // Autoplay blocked; will start on first pointerdown.
    }
  }

  function unlockAudioOnce(): void {
    if (isReady) return;
    startExperience().catch(() => {});
  }

  async function togglePlayback(): Promise<void> {
    if (!isReady) {
      await startExperience();
      return;
    }
    if (audio.isPlaying) {
      audio.pause();
      isPlaying = false;
    } else {
      await audio.play();
      isPlaying = audio.isPlaying;
    }
  }

  function advanceScene(rotateAudio: boolean): void {
    sceneIndex = (sceneIndex + 1) % SCENES.length;

    if (rotateAudio && isReady) audio.setTrack(pickRandomTrack(audio.currentTrack));

    storyVisible = false;
    const requestId = ++quoteRequestId;

    loadQuote().then((quote) => {
      if (requestId !== quoteRequestId) return;
      setTimeout(() => {
        if (requestId !== quoteRequestId) return;
        setQuote(quote);
        storyVisible = true;
      }, 1400);
    });
  }

  function switchPreset(): void {
    advanceScene(true);
  }

  function advanceTrack(): void {
    if (!isReady) return;
    audio.setTrack(pickRandomTrack(audio.currentTrack));
  }

  onMount(() => {
    try {
      audio = new AudioEngine();
      audio.onEnded = advanceTrack;
      audio.setTrack(AUDIO_TRACKS[Math.floor(Math.random() * AUDIO_TRACKS.length)]);
    } catch (error) {
      console.error("Wolpsflow startup failed", error);
      return;
    }

    refreshQuote();

    window.addEventListener("pointerdown", unlockAudioOnce, { once: true });
    const autoplayTimer = window.setTimeout(tryAutoplay, 120);
    const sceneTimer = window.setInterval(() => advanceScene(false), 15 * 60 * 1000);

    return () => {
      window.clearTimeout(autoplayTimer);
      window.clearInterval(sceneTimer);
      window.removeEventListener("pointerdown", unlockAudioOnce);
    };
  });

  onDestroy(() => {
    audio?.dispose();
  });
</script>

<div class="scene-stage" aria-hidden="true">
  {#each SCENES as src, index}
    <div
      class="scene-layer"
      class:is-active={index === sceneIndex}
      style:background-image="url({src})"
    ></div>
  {/each}
  <div class="scene-tint"></div>
</div>

<main class="interface" aria-label="Wolpsflow music space">
  <header class="story-panel">
    <div class="station-line">
      <button
        class:is-playing={isPlaying}
        class="tiny-icon"
        type="button"
        title="Play or pause"
        aria-label={isPlaying ? "Pause" : "Play"}
        on:click={togglePlayback}
      >
        <svg class="play-icon" aria-hidden="true" viewBox="0 0 24 24">
          <path d="m8 5 11 7-11 7V5Z" />
        </svg>
        <svg class="pause-icon" aria-hidden="true" viewBox="0 0 24 24">
          <path d="M8 5v14" />
          <path d="M16 5v14" />
        </svg>
      </button>
      <strong>Wolpsflow</strong>
    </div>

    <p class="story-text" class:visible={storyVisible}>{storyLine}</p>
  </header>

  <button class="next-link" type="button" on:click={switchPreset}>
    Next
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="m6 9 6 6 6-6" />
    </svg>
  </button>
</main>
